import axios from '../axios';
import * as html from '../html';

interface getArtworkParams {
    page?: number;
    pageSize?: number;
    tags?: string | string[];
    groupTags?: string | string[];
    company?: string | string[];
    name?: string | string[];
    group?: string | string[];
    reverse?: boolean;
}

declare interface file {
    file: string;
    size: number;
    width: number;
    height: number;
    mime: string;
}

type artworkHTMLFunc = (fancybox?: string, url?: string | false) => string;

export declare interface ArtworkGroupImage {
    name: string;
    group: string;
    description?: string;
    url?: string;
    path: string;
    tags?: string[];
    groupTags?: string[];

    large?: file;
    large2x?: file;
    largeWebP?: file;
    largeWebP2x?: file;
    largeAV1?: file;
    largeAV12x?: file;
    main?: file;
    main2x?: file;
    mainWebP?: file;
    mainWebP2x?: file;
    mainAV1?: file;
    mainAV12x?: file;
    original: file;
    raster?: file;
    thumbnail?: file;

    ArtworkHTML: artworkHTMLFunc;

    cdn?: string;
}

declare interface getArtworkResp {
    artwork: ArtworkGroupImage[];
    cdn: string;
}

export function ArtworkHTML(cdn: string, a: ArtworkGroupImage, fancybox?: string, url: string | false | undefined = a.url): string {
    if (!a.main) {
        return '';
    }

    const sources: { x1: string; x2?: string; size: number; mime: string }[] = [];

    if (a.main && a.main2x && a.main.mime) {
        sources.push({
            x1: a.main.file,
            x2: a.main2x.file,
            size: a.main.size,
            mime: a.main.mime,
        });
    }

    if (a.mainWebP && a.mainWebP2x) {
        sources.push({
            x1: a.mainWebP.file,
            x2: a.mainWebP2x.file,
            size: a.mainWebP.size,
            mime: a.mainWebP.mime || 'image/webp',
        });
    }

    if (a.mainAV1 && a.mainAV12x) {
        sources.push({
            x1: a.mainAV1.file,
            x2: a.mainAV12x.file,
            size: a.mainAV1.size,
            mime: a.mainAV1.mime || 'image/avif',
        });
    }

    sources.sort((a, b) => {
        if (a.size < b.size) {
            return -1;
        }
        if (a.size > b.size) {
            return 1;
        }
        return 0;
    });

    if (a.original.mime === 'image/svg+xml') {
        sources.unshift({
            x1: a.original.file,
            size: a.original.size,
            mime: a.original.mime,
        });
    }

    let sourcesHTML = '';
    for (const s of sources) {
        sourcesHTML += /*html*/`<source srcset="${html.Escape(`${cdn}/${a.path}/${s.x1}${s.x2 ? `, ${cdn}/${a.path}/${s.x2} 2x` : ''}`)}" type="${html.Escape(s.mime)}">`;
    }

    const picture = `
    <picture>
        <!--[if IE 9]><video style=display:none><![endif]-->
        ${sourcesHTML}
        <!--[if IE 9]></video><![endif]-->
        <img width="${a.main.width}" height="${a.main.height}"
            alt="${html.Escape(a.name)}"
            title="${html.Escape(a.description || a.name)}"
            src="${html.Escape(`${cdn}/${a.path}/${a.main.file}`)}"
            srcset="${html.Escape(`${cdn}/${a.path}/${a.main.file}${a.main2x ? `, ${cdn}/${a.path}/${a.main2x.file} 2x` : ''}`)}">
    </picture>`;

    if (url !== false) {
        return `
        <a href="${html.Escape(a.url || `${cdn}/${a.path}/${a.raster?.file || a.original.file}`)}"${!a.url && fancybox ? ` data-fancybox="${html.Escape(fancybox)}"` : ''}>
            ${picture}
        </a>`;
    }

    return picture;
}

export async function GetArtwork(params: getArtworkParams): Promise<ArtworkGroupImage[]> {
    const resp = await axios.get('/artwork', {
        params: {
            'page': params.page,
            'pageSize': params.pageSize,
            'tag': params.tags,
            'groupTag': params.groupTags,
            'company': params.company,
            'name': params.name,
            'group': params.group,
            'reverse': params.reverse,
        },
    });
    const d = resp.data as getArtworkResp;

    for (const a of d.artwork) {
        a.ArtworkHTML = (fancybox?: string, url?: string | false) => ArtworkHTML(d.cdn, a, fancybox, url);
        a.cdn = d.cdn;
    }

    return d.artwork;
}

export async function GetArtworkByName(params: getArtworkParams): Promise<Record<string, ArtworkGroupImage>> {
    const artwork = await GetArtwork(params);

    const map: Record<string, ArtworkGroupImage> = {};

    for (const a of artwork) {
        // we don't want to override names here, since the first
        // ones will be the ones we want to show in all likelihood
        if (a.name in map) {
            continue;
        }

        map[a.name] = a;
    }

    return map;
}

export async function GetArtworkByGroupName(params: getArtworkParams): Promise<Record<string, Record<string, ArtworkGroupImage>>> {
    const artwork = await GetArtwork(params);

    const map: Record<string, Record<string, ArtworkGroupImage>> = {};

    for (const a of artwork) {
        let group = map[a.group];
        if (!group) {
            group = {};
            map[a.group] = group;
        }

        // we don't want to override names here, since the first
        // ones will be the ones we want to show in all likelihood
        if (a.name in group) {
            continue;
        }

        group[a.name] = a;
    }

    return map;
}